import { Observable } from "rxjs";
import siteConfig from "site-config";
import {
    BUYER_DATA,
    COMPANY_DATA,
    INVENTORY_DATA,
    PRICE_SHEETS,
    PRODUCT_DATA,
    PRODUCT_IMAGES,
} from "constants/action-types";
import { defaultCheckError, IRequestDataAction, progressEpic } from "../base-epic";
import { fetchInventoryDataUploadJob, startInventoryDataUploadJobPolling } from "../../actions/inventory-data-actions";
import { fetchBuyerDataUploadJob, startBuyerDataUploadJobPolling } from "../../actions/buyer-data-actions";
import { fetchCompanyDataUploadJob, startCompanyDataUploadJobPolling } from "../../actions/company-data-actions";
import { fetchProductDataUploadJob, startProductDataUploadJobPolling } from "../../actions/product-data-actions";
import { fetchProductImageUploadJob, startProductImageUploadJobPolling } from "../../actions/product-images-actions";
import { fetchPriceSheetsUploadJob, startPriceSheetsUploadJobPolling } from "../../actions/price-sheets-actions";
import { IUploadActionTypes } from "reducers/base-upload-reducers";

export enum JobStatusMap {
    CANCELED = 30,
    DEADLINE_EXCEEDED = 34,
    FAILED = 40,
    SUCCESS = 50,
}

export type IStartUploadJobActionPayload = IRequestDataAction<FormData>;
export type IFetchJobActionPayload = IRequestDataAction<{ jobId: string; pipelineId: string }>;

// shared among endpoints
export type IUploadResponse = {
    message: string;
    localizedMessage?: string;
    job?: any;
    errors?: string[];
};

type IExportResponse = {
    message: string;
    localizedMessage?: string;
    job?: any;
    errors?: string[];
};

const uploadProgressEpicFactory = (TYPE: IUploadActionTypes, finishActions) =>
    progressEpic<IStartUploadJobActionPayload, IUploadResponse>(
        TYPE.UPLOAD,
        ({ payload }) => ({
            method: "POST",
            url: siteConfig.api + payload.url,
            body: payload.body,
            headers: {
                "Content-Type": "application/json",
                "X-Requested-With": "XMLHttpRequest",
                module: "default",
            },
        }),
        {
            dataCustomizer: (response: IUploadResponse) => ({
                errors: response.errors,
                message: response.localizedMessage,
                job: response.job,
            }),
            checkErrorStatus: defaultCheckError,
            finishActions,
        }
    );

export const productImagesUploadEpic = uploadProgressEpicFactory(PRODUCT_IMAGES, (response) => [
    { type: PRODUCT_IMAGES.PROCESS.START },
    fetchProductImageUploadJob(response.job.id, response.job.pipelineId),
]);

export const productDataUploadEpic = uploadProgressEpicFactory(PRODUCT_DATA, (response) => [
    { type: PRODUCT_DATA.PROCESS.START },
    fetchProductDataUploadJob(response.job.id, response.job.pipelineId),
]);

export const companyDataUploadEpic = uploadProgressEpicFactory(COMPANY_DATA, (response) => [
    { type: COMPANY_DATA.PROCESS.START },
    fetchCompanyDataUploadJob(response.job.id, response.job.pipelineId),
]);

export const buyerDataUploadEpic = uploadProgressEpicFactory(BUYER_DATA, (response) => [
    { type: BUYER_DATA.PROCESS.START },
    fetchBuyerDataUploadJob(response.job.id, response.job.pipelineId),
]);

export const inventoryDataUploadEpic = uploadProgressEpicFactory(INVENTORY_DATA, (response) => [
    { type: INVENTORY_DATA.PROCESS.START },
    fetchInventoryDataUploadJob(response.job.id, response.job.pipelineId),
]);

const POLL_INTERVAL = 2500; // 2.5 sec

export const priceSheetUploadEpic = uploadProgressEpicFactory(PRICE_SHEETS, (response) => [
    { type: PRICE_SHEETS.PROCESS.START },
    fetchPriceSheetsUploadJob(response.job.id, response.job.pipelineId),
]);

const pollJobEpicFactory = (TYPE: IUploadActionTypes, fetchAction) => (action$) =>
    action$.ofType(TYPE.JOB.POLL.START).mergeMap(({ payload }) =>
        Observable.timer(POLL_INTERVAL)
            .takeUntil(action$.ofType(TYPE.JOB.POLL.STOP))
            .flatMap(() => [fetchAction(payload.jobId, payload.pipelineId)])
    );

export const pollImagesUploadJobEpic = pollJobEpicFactory(PRODUCT_IMAGES, fetchProductImageUploadJob);

export const pollProductDataUploadJobEpic = pollJobEpicFactory(PRODUCT_DATA, fetchProductDataUploadJob);

export const pollCompanyDataUploadJobEpic = pollJobEpicFactory(COMPANY_DATA, fetchCompanyDataUploadJob);

export const pollBuyerDataUploadJobEpic = pollJobEpicFactory(BUYER_DATA, fetchBuyerDataUploadJob);

const jobEndStatuses = [
    JobStatusMap.CANCELED,
    JobStatusMap.DEADLINE_EXCEEDED,
    JobStatusMap.FAILED,
    JobStatusMap.SUCCESS,
];

export const pollPriceSheetsUploadJobEpic = pollJobEpicFactory(PRICE_SHEETS, fetchPriceSheetsUploadJob);

export const pollInventoryUploadJobEpic = pollJobEpicFactory(INVENTORY_DATA, fetchInventoryDataUploadJob);

const fetchJobEpicFactory = (TYPE: IUploadActionTypes, finishActions) =>
    progressEpic<IFetchJobActionPayload, IUploadResponse>(
        TYPE.JOB.FETCH,
        ({ payload }) => ({
            method: "POST",
            url: siteConfig.api + payload.url,
            body: payload.body,
            headers: {
                "X-Requested-With": "XMLHttpRequest",
                module: "default",
            },
        }),
        {
            dataCustomizer: (response: IUploadResponse) => {
                return { errors: response.errors, message: response.localizedMessage, job: response.job };
            },
            checkErrorStatus: defaultCheckError,
            finishActions,
        }
    );

export const fetchImagesJobEpic = fetchJobEpicFactory(PRODUCT_IMAGES, (response) =>
    jobEndStatuses.includes(response.job.status)
        ? [{ type: PRODUCT_IMAGES.PROCESS.SUCCESS }]
        : [startProductImageUploadJobPolling({ jobId: response.job.id, pipelineId: response.job.pipelineId })]
);

export const fetchProductDataJobEpic = fetchJobEpicFactory(PRODUCT_DATA, (response) =>
    jobEndStatuses.includes(response.job.status)
        ? [{ type: PRODUCT_DATA.PROCESS.SUCCESS }]
        : [startProductDataUploadJobPolling({ jobId: response.job.id, pipelineId: response.job.pipelineId })]
);

export const fetchCompanyDataJobEpic = fetchJobEpicFactory(COMPANY_DATA, (response) =>
    jobEndStatuses.includes(response.job.status)
        ? [{ type: COMPANY_DATA.PROCESS.SUCCESS }]
        : [startCompanyDataUploadJobPolling({ jobId: response.job.id, pipelineId: response.job.pipelineId })]
);

export const fetchBuyerDataJobEpic = fetchJobEpicFactory(BUYER_DATA, (response) =>
    jobEndStatuses.includes(response.job.status)
        ? [{ type: BUYER_DATA.PROCESS.SUCCESS }]
        : [startBuyerDataUploadJobPolling({ jobId: response.job.id, pipelineId: response.job.pipelineId })]
);

export const fetchInventoryDataJobEpic = fetchJobEpicFactory(INVENTORY_DATA, (response) =>
    jobEndStatuses.includes(response.job.status)
        ? [{ type: INVENTORY_DATA.PROCESS.SUCCESS }]
        : [startInventoryDataUploadJobPolling({ jobId: response.job.id, pipelineId: response.job.pipelineId })]
);

export const fetchPriceSheetsJobEpic = fetchJobEpicFactory(PRICE_SHEETS, (response) =>
    jobEndStatuses.includes(response.job.status)
        ? [{ type: PRICE_SHEETS.PROCESS.SUCCESS }]
        : [startPriceSheetsUploadJobPolling({ jobId: response.job.id, pipelineId: response.job.pipelineId })]
);
export const productDataExportEpic = progressEpic<IRequestDataAction, IExportResponse>(
    PRODUCT_DATA.EXPORT.DOWNLOAD,
    ({ payload }) => ({
        method: "POST",
        url: siteConfig.api + payload.url,
        body: payload.body,
    }),
    {
        checkErrorStatus: defaultCheckError,
    }
);
